<template>
  <header :class="headerClass">
    <div class="w-full px-4 py-4 flex items-center justify-between">
      <router-link v-if="shouldLogoRedirect" :to="dashboardRoute" class="flex-shrink-0">
        <img :src="logoSrc" alt="GDExpert" class="h-14 w-auto object-contain cursor-pointer" />
      </router-link>
      <img v-else :src="logoSrc" alt="GDExpert" class="h-14 w-auto object-contain flex-shrink-0" />
      
      <!-- Menu utilisateur avec avatar -->
      <div v-if="showAccountButton" class="relative">
        <button @click="toggleMenu" class="flex items-center gap-2 px-3 py-2 rounded-full bg-[#f39c12] text-white hover:bg-[#e67e22] transition-colors">
          <div class="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-sm">
            {{ userInitials }}
          </div>
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
        
        <!-- Dropdown menu -->
        <div v-if="menuOpen" class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
          <router-link to="/compte" @click="menuOpen = false" class="block px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors">
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
              </svg>
              Mon compte
            </div>
          </router-link>
          <button @click="logout" class="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors">
            <div class="flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
              </svg>
              Se déconnecter
            </div>
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import logo from '../logo.svg'

export default {
  name: 'Header',
  data() {
    return {
      isLoggedIn: false,
      menuOpen: false,
      /** Copie réactive du JSON `user` (localStorage n'est pas réactif pour Vue). */
      storedUserJson: null
    }
  },
  computed: {
    logoSrc() {
      return logo
    },
    showAccountButton() {
      return this.isLoggedIn && !!this.storedUserJson
    },
    userInitials() {
      try {
        const raw = this.storedUserJson
        if (raw) {
          const u = JSON.parse(raw)
          const first = (u.firstName || u.first_name || '?')[0].toUpperCase()
          const last = (u.lastName || u.last_name || '?')[0].toUpperCase()
          return first + last
        }
      } catch (e) {}
      return '??'
    },
    dashboardRoute() {
      try {
        const raw = this.storedUserJson
        if (raw) {
          const u = JSON.parse(raw)
          // Rediriger selon le rôle
          const role = u && u.role ? String(u.role).toLowerCase().trim() : null
          if (role === 'admin') {
            return '/admin/dashboard'
          } else if (role === 'uploader') {
            return '/uploader/dashboard'
          }
          // Pour tous les autres rôles (partner, null, undefined, etc.), rediriger vers /dashboard
          return '/dashboard'
        }
      } catch(e) {
        console.error('Erreur lors de la lecture du rôle utilisateur:', e)
      }
      // Par défaut, rediriger vers le dashboard partenaire
      return '/dashboard'
    },
    isDashboard() {
      return this.$route.name === 'Dashboard'
    },
    isAdmin() {
      try {
        const raw = this.storedUserJson
        if (raw) {
          const u = JSON.parse(raw)
          const role = u && u.role ? String(u.role).toLowerCase().trim() : null
          return role === 'admin'
        }
      } catch(e) {
        console.error('Erreur lors de la lecture du rôle utilisateur:', e)
      }
      return false
    },
    shouldLogoRedirect() {
    // Logo cliquable pour tout utilisateur connecté (partenaire, admin, uploader)
      // La destination est déterminée par dashboardRoute selon le rôle
      return this.isLoggedIn
    },
    headerClass() {
      return 'absolute inset-x-0 top-0 z-20 bg-transparent border-b border-gray-300/30'
    }
  },
  methods: {
    handleClickOutside(e) {
      if (this.menuOpen && this.$el && !this.$el.contains(e.target)) {
        this.menuOpen = false
      }
    },
    toggleMenu() {
      this.menuOpen = !this.menuOpen
    },
    logout() {
      try {
        localStorage.removeItem('auth_success')
        localStorage.removeItem('user')
        localStorage.removeItem('token')
      } catch (e) {}
      try {
        window.dispatchEvent(new Event('storage'))
        window.dispatchEvent(new CustomEvent('portail-auth-sync'))
      } catch (e) {}
      this.menuOpen = false
      this.syncAuth()
      this.$router.push('/')
    },
    syncAuth() {
      try {
        this.isLoggedIn = localStorage.getItem('auth_success') === 'true'
        this.storedUserJson = localStorage.getItem('user')
      } catch (e) {
        this.isLoggedIn = false
        this.storedUserJson = null
      }
    }
  },
  created() {
    this.syncAuth()
  },
  mounted() {
    window.addEventListener('storage', this.syncAuth)
    window.addEventListener('portail-auth-sync', this.syncAuth)
    this._removeAfterEach = this.$router.afterEach(() => {
      this.syncAuth()
    })
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    window.removeEventListener('storage', this.syncAuth)
    window.removeEventListener('portail-auth-sync', this.syncAuth)
    if (typeof this._removeAfterEach === 'function') {
      this._removeAfterEach()
    }
    document.removeEventListener('click', this.handleClickOutside)
  }
}
</script>